import classes from 'styles/components/Roulette.module.scss';
import { RouletteFrenchCombo } from '../enums/RouletteFrenchCombo';
import { InsideBetColor } from '../enums/RouletteInsideBetColor';
import { OutSideBetAreas } from '../enums/OutSideBetAreas';
import { TEXT_ON_TABLE } from '../constants/rouletteTextOnTable';
import {
    IInsideBet,
    IFrenchBets,
    IOutsideBets,
    IInsideMultiBetArea,
} from '../interfaces/RNGRoulette';
import { ItemAreaCheck } from './ItemAreaCheck';

export class RouletteDefinition {
    private static insideBetsCollection: Omit<IInsideBet, 'next' | 'previous'>[] = [
        { value: 0, color: classes.colorGreen, colorClassName: InsideBetColor.GREEN },
        { value: 1, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 2, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 3, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 4, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 5, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 6, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 7, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 8, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 9, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 10, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 11, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 12, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 13, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 14, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 15, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 16, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 17, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 18, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 19, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 20, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 21, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 22, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 23, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 24, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 25, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 26, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 27, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 28, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 29, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 30, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 31, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 32, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 33, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 34, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        { value: 35, color: classes.colorWhite, colorClassName: InsideBetColor.BLACK },
        { value: 36, color: classes.colorRed, colorClassName: InsideBetColor.RED },
        {
            value: 37,
            color: classes.colorGreen,
            colorClassName: InsideBetColor.GREEN,
            valueText: TEXT_ON_TABLE.DOUBLE_ZERO,
        }, // 00 for American Roulette
    ];

    public static isZeroSector = (value: number) => [0, 37].includes(value);

    public static generateInsideBets(order: number[]): IInsideBet[] {
        return order
            .map((item) => ({ ...RouletteDefinition.insideBetsCollection[item] }))
            .map((insideBet, index, array) => {
                Object.assign(insideBet, {
                    next: array[index + 1] || array[0],
                    previous: array[index - 1] || array[array.length - 1],
                });

                return insideBet as IInsideBet;
            });
    }

    public static generateInsideBetsMap = <T extends { value: number }>(insideBets: T[]) =>
        insideBets.reduce<Record<IInsideBet['value'], T>>((acc, currentName) => {
            acc[currentName.value] = currentName;
            return acc;
        }, {});

    public static generateCommonInsideMultiBetsMap = (insideBets: IInsideBet[]) =>
        insideBets.reduce<Record<number, Partial<Record<IInsideMultiBetArea, number[]>>>>(
            (finalValue, sector) => {
                const { value } = sector;

                if (RouletteDefinition.isZeroSector(value)) {
                    return finalValue;
                }

                finalValue[value] = {};

                if (value % 3 === 1) {
                    // street bet
                    finalValue[value].street = [value, value + 1, value + 2];
                } else {
                    // split bet (side)
                    finalValue[value].splitSide = [value - 1, value];
                }

                if (value <= 33) {
                    // split bet (below)
                    finalValue[value].splitBelow = [value, value + 3];

                    if (value % 3 === 1) {
                        // double street bet
                        finalValue[value].doubleStreet = Array(6)
                            .fill(value)
                            .map((newValue, index) => newValue + index);
                    } else {
                        // square bet
                        finalValue[value].square = [value - 1, value, value + 2, value + 3];
                    }
                }

                return finalValue;
            },
            {},
        );

    private static insideBetsCollectionMap = RouletteDefinition.generateInsideBetsMap(
        RouletteDefinition.insideBetsCollection,
    );

    public static outsideBets: IOutsideBets = (() => {
        const numbers = RouletteDefinition.insideBetsCollection
            .filter((insideBet) => insideBet.value !== 0 && insideBet.value !== 37)
            .map((insideBet) => insideBet.value)
            .sort((a, b) => a - b);

        return {
            [OutSideBetAreas.firstDozen]: numbers.filter(ItemAreaCheck.isFirstDozen),
            [OutSideBetAreas.secondDozen]: numbers.filter(ItemAreaCheck.isSecondDozen),
            [OutSideBetAreas.thirdDozen]: numbers.filter(ItemAreaCheck.isThirdDozen),
            [OutSideBetAreas.firstColumn]: numbers.filter(ItemAreaCheck.isFirstColumn),
            [OutSideBetAreas.secondColumn]: numbers.filter(ItemAreaCheck.isSecondColumn),
            [OutSideBetAreas.thirdColumn]: numbers.filter(ItemAreaCheck.isThirdColumn),
            [OutSideBetAreas.low]: numbers.filter((number) => number <= 18),
            [OutSideBetAreas.even]: numbers.filter((number) => number % 2 === 0),
            [OutSideBetAreas.red]: numbers.filter(
                (number) =>
                    // Outside bets are the same for both American and European roulette
                    RouletteDefinition.insideBetsCollectionMap[number].colorClassName ===
                    InsideBetColor.RED,
            ),
            [OutSideBetAreas.black]: numbers.filter(
                (number) =>
                    RouletteDefinition.insideBetsCollectionMap[number].colorClassName ===
                    InsideBetColor.BLACK,
            ),
            [OutSideBetAreas.odd]: numbers.filter((number) => number % 2 === 1),
            [OutSideBetAreas.high]: numbers.filter((number) => number > 18),
        };
    })();

    public static frenchBets: IFrenchBets = {
        [RouletteFrenchCombo.TIER]: [27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33],
        [RouletteFrenchCombo.ORPHELINS]: [1, 20, 14, 31, 9, 17, 34, 6],
        [RouletteFrenchCombo.VOISINS]: [
            22, 18, 29, 7, 28, 12, 35, 3, 26, 0, 32, 15, 19, 4, 21, 2, 25,
        ],
        [RouletteFrenchCombo.ZERO]: [12, 35, 3, 26, 0, 32, 15],
    };
}
